/**
 * Qui a le droit de quoi.
 *
 * Le réflexe habituel est d'écrire la liste des rôles autorisés sur chaque route :
 * `@Roles('ADMIN', 'MAITRE_DE_CHAI')`. Au troisième rôle, il faut rouvrir les trente
 * routes et espérer n'en oublier aucune — et rien ne dit, en lisant une route, *pourquoi*
 * ces rôles-là. On nomme donc ce que l'on protège, jamais qui y accède.
 *
 * Cette table est partagée : l'API l'emploie pour interdire, le front pour ne pas
 * proposer. Deux tables auraient fini par diverger, et un bouton qui mène à un 403 est
 * une promesse rompue.
 */
export const CAPABILITIES = [
    /** Les réglages du cercle : récurrence, lieu, nombre d'invités. */
    'settings:manage',
    /** Supprimer — c'est-à-dire anonymiser — un membre. Irréversible. */
    'member:delete',
    /** Nommer ou révoquer un maître de chai. */
    'member:promote',
    /** Inviter, réactiver, désactiver un membre. */
    'member:manage',
    /** Créer, modifier, supprimer une séance ; rattacher, détacher, réordonner ses rhums. */
    'session:manage',
    /** Ouvrir les notes, révéler un rhum, clôturer. */
    'session:conduct',
    /** Renommer ou fusionner un terme du référentiel. */
    'reference:manage',
    /** Corriger la fiche d'un rhum qu'on n'a pas créé. */
    'rum:moderate',
    /** Apporter un rhum à une séance où l'on est inscrit. */
    'session:contribute',
];
/**
 * La table, dans l'ordre du plus restreint au plus commun.
 *
 * Elle est exhaustive et sans héritage : `MAITRE_DE_CHAI` ne « contient » pas
 * `BOUILLEUR`, il énumère ses propres droits. Une hiérarchie implicite se lit vite mais
 * se relit mal — « le maître de chai peut-il supprimer un membre ? » doit se répondre en
 * regardant une ligne, pas en remontant une chaîne.
 */
const GRANTS = {
    'settings:manage': ['ADMIN'],
    'member:delete': ['ADMIN'],
    'member:promote': ['ADMIN'],
    'member:manage': ['ADMIN', 'MAITRE_DE_CHAI'],
    'session:manage': ['ADMIN', 'MAITRE_DE_CHAI'],
    'session:conduct': ['ADMIN', 'MAITRE_DE_CHAI'],
    'reference:manage': ['ADMIN', 'MAITRE_DE_CHAI'],
    'rum:moderate': ['ADMIN', 'MAITRE_DE_CHAI'],
    'session:contribute': ['ADMIN', 'MAITRE_DE_CHAI', 'BOUILLEUR'],
};
/** Le rôle donné porte-t-il cette capacité ? */
export function can(role, capability) {
    return GRANTS[capability].includes(role);
}
/**
 * Toutes les capacités d'un rôle.
 *
 * Sert au front, qui reçoit la liste une fois à la connexion plutôt que d'interroger
 * `can()` à chaque rendu.
 */
export function capabilitiesOf(role) {
    return CAPABILITIES.filter((capability) => can(role, capability));
}
/**
 * Corriger une fiche de rhum : celui qui l'a saisie, ou celui qui porte la modération.
 *
 * Une capacité ne suffit pas à exprimer cette règle, parce qu'elle dépend de la *ligne* et
 * pas seulement du rôle : un bouilleur complète la fiche qu'il a créée en séance, sans quoi
 * personne ne créerait plus rien. Le prédicat vit donc ici, à côté de la table, plutôt que
 * dans l'API : c'est le seul moyen que le bouton du front et le refus du serveur disent la
 * même chose.
 */
export function canEditRum(role, actorId, createdById) {
    return can(role, 'rum:moderate') || createdById === actorId;
}
/**
 * Retirer un lien utile ou un commentaire d'une fiche : son auteur, ou la modération.
 *
 * Même forme que `canEditRum`, mais l'appartenance porte sur la contribution et non sur la
 * fiche : sur la fiche d'un rhum qu'il n'a pas créé, un bouilleur retire son propre
 * commentaire et rien d'autre. La capacité réemployée est `rum:moderate` — modérer une fiche
 * et modérer ce qu'on y accroche est le même geste, et inventer une capacité de plus
 * obligerait à rouvrir la table des refus sans rien clarifier.
 */
export function canRemoveRumContribution(role, actorId, authorId) {
    return can(role, 'rum:moderate') || (authorId !== null && authorId === actorId);
}
//# sourceMappingURL=permissions.js.map